/**
 * Custom theme engine.
 *
 * The `custom` (light) and `custom-dark` themes are user-editable. Their base
 * palettes live in globals.css; this module lets the user override any token
 * at runtime by injecting a <style> block that wins on specificity by virtue
 * of coming later in the cascade.
 *
 * Values are stored as hex because <input type="color"> speaks hex. CSS custom
 * properties accept hex just as happily as the oklch() defaults in globals.css.
 */

export type ThemeMode = "light" | "dark"

/** A single editable design token. */
export interface TokenDef {
    /** CSS custom property name, without the leading `--`. */
    key: string
    label: string
    /** Shown under the label to explain where the token actually lands. */
    hint?: string
}

export interface TokenGroup {
    title: string
    description: string
    tokens: TokenDef[]
}

/**
 * Grouped so the editor reads as a design system rather than a wall of pickers.
 * Every token here is one the app's components genuinely consume.
 */
export const TOKEN_GROUPS: TokenGroup[] = [
    {
        title: "Brand",
        description: "The primary colour and the text that sits on top of it.",
        tokens: [
            { key: "primary", label: "Primary", hint: "Buttons, active states, links" },
            { key: "primary-foreground", label: "On primary", hint: "Text on primary surfaces" },
            { key: "ring", label: "Focus ring", hint: "Keyboard focus outline" },
        ],
    },
    {
        title: "Surfaces",
        description: "Page and panel backgrounds.",
        tokens: [
            { key: "background", label: "Background", hint: "Page canvas" },
            { key: "foreground", label: "Body text" },
            { key: "card", label: "Card", hint: "Panels, tables, list rows" },
            { key: "card-foreground", label: "Card text" },
            { key: "popover", label: "Popover", hint: "Dialogs, dropdowns, menus" },
            { key: "popover-foreground", label: "Popover text" },
        ],
    },
    {
        title: "Support",
        description: "Secondary, muted and accent surfaces.",
        tokens: [
            { key: "secondary", label: "Secondary" },
            { key: "secondary-foreground", label: "On secondary" },
            { key: "muted", label: "Muted", hint: "Subtle fills, table headers" },
            { key: "muted-foreground", label: "Muted text", hint: "Captions, placeholders" },
            { key: "accent", label: "Accent", hint: "Hover states" },
            { key: "accent-foreground", label: "On accent" },
        ],
    },
    {
        title: "Lines & status",
        description: "Borders, inputs and destructive actions.",
        tokens: [
            { key: "border", label: "Border" },
            { key: "input", label: "Input border" },
            { key: "destructive", label: "Destructive", hint: "Delete and error actions" },
            { key: "destructive-foreground", label: "On destructive" },
        ],
    },
    {
        title: "Sidebar",
        description: "The dashboard navigation rail.",
        tokens: [
            { key: "sidebar", label: "Sidebar" },
            { key: "sidebar-foreground", label: "Sidebar text" },
            { key: "sidebar-primary", label: "Sidebar primary" },
            { key: "sidebar-accent", label: "Sidebar hover" },
            { key: "sidebar-accent-foreground", label: "On sidebar hover" },
            { key: "sidebar-border", label: "Sidebar border" },
        ],
    },
]

export const ALL_TOKEN_KEYS: string[] = TOKEN_GROUPS.flatMap((g) => g.tokens.map((t) => t.key))

export type TokenValues = Record<string, string>

/**
 * Seed palettes. These mirror the `.custom` / `.custom-dark` blocks in
 * globals.css, converted to hex so the pickers have a sane starting point.
 */
export const DEFAULT_THEME: Record<ThemeMode, TokenValues> = {
    light: {
        primary: "#8047e1",
        "primary-foreground": "#fdfcff",
        ring: "#8047e1",
        background: "#fcfbff",
        foreground: "#1c1827",
        card: "#ffffff",
        "card-foreground": "#1c1827",
        popover: "#ffffff",
        "popover-foreground": "#1c1827",
        secondary: "#f3f1fa",
        "secondary-foreground": "#2f2740",
        muted: "#f4f2fa",
        "muted-foreground": "#6d6483",
        accent: "#e9e3f8",
        "accent-foreground": "#241d33",
        border: "#e2ddef",
        input: "#e2ddef",
        destructive: "#c62828",
        "destructive-foreground": "#ffffff",
        sidebar: "#f8f6fd",
        "sidebar-foreground": "#3a3150",
        "sidebar-primary": "#8047e1",
        "sidebar-accent": "#e7e0f7",
        "sidebar-accent-foreground": "#2f2740",
        "sidebar-border": "#e2dcef",
    },
    dark: {
        primary: "#a37aff",
        "primary-foreground": "#14101f",
        ring: "#9a6ef5",
        background: "#0e0c15",
        foreground: "#f6f4fb",
        card: "#181524",
        "card-foreground": "#f6f4fb",
        popover: "#181524",
        "popover-foreground": "#f6f4fb",
        secondary: "#241f33",
        "secondary-foreground": "#f6f4fb",
        muted: "#241f33",
        "muted-foreground": "#a9a2bd",
        accent: "#2e2740",
        "accent-foreground": "#f6f4fb",
        border: "#2b2540",
        input: "#332c4a",
        destructive: "#f16d6d",
        "destructive-foreground": "#14101f",
        sidebar: "#131020",
        "sidebar-foreground": "#ebe8f5",
        "sidebar-primary": "#a37aff",
        "sidebar-accent": "#271f3a",
        "sidebar-accent-foreground": "#f6f4fb",
        "sidebar-border": "#2b2540",
    },
}

export const STORAGE_KEY = "satbuddy-custom-theme"
export const STYLE_ELEMENT_ID = "satbuddy-custom-theme-vars"

/** Deep-clones the defaults so callers can mutate freely. */
export function defaultTheme(): Record<ThemeMode, TokenValues> {
    return {
        light: { ...DEFAULT_THEME.light },
        dark: { ...DEFAULT_THEME.dark },
    }
}

/**
 * Merges stored values over the defaults, dropping anything that isn't a known
 * token or a valid hex colour. Storage is user-writable, so treat it as input.
 */
export function normalizeTheme(raw: unknown): Record<ThemeMode, TokenValues> {
    const result = defaultTheme()
    if (!raw || typeof raw !== "object") return result

    const known = new Set(ALL_TOKEN_KEYS)
    const isHex = (v: unknown): v is string =>
        typeof v === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v)

    for (const mode of ["light", "dark"] as ThemeMode[]) {
        const section = (raw as Record<string, unknown>)[mode]
        if (!section || typeof section !== "object") continue
        for (const [key, value] of Object.entries(section as Record<string, unknown>)) {
            if (known.has(key) && isHex(value)) result[mode][key] = value
        }
    }
    return result
}

/** Builds the CSS text that overrides the globals.css defaults. */
export function buildThemeCss(theme: Record<ThemeMode, TokenValues>): string {
    const decls = (values: TokenValues) =>
        ALL_TOKEN_KEYS.filter((k) => values[k]).map((k) => `--${k}:${values[k]};`).join("")

    return `.custom{${decls(theme.light)}}.custom-dark{${decls(theme.dark)}}`
}

export function loadTheme(): Record<ThemeMode, TokenValues> {
    if (typeof window === "undefined") return defaultTheme()
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY)
        return stored ? normalizeTheme(JSON.parse(stored)) : defaultTheme()
    } catch {
        return defaultTheme()
    }
}

/** Writes the theme to storage and repaints the live <style> block. */
export function saveTheme(theme: Record<ThemeMode, TokenValues>): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme))
    } catch {
        // Storage can be full or blocked; the in-page style below still applies.
    }
    applyTheme(theme)
}

/** Injects (or updates) the override <style> so edits show up immediately. */
export function applyTheme(theme: Record<ThemeMode, TokenValues>): void {
    if (typeof document === "undefined") return
    let el = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null
    if (!el) {
        el = document.createElement("style")
        el.id = STYLE_ELEMENT_ID
        document.head.appendChild(el)
    }
    el.textContent = buildThemeCss(theme)
}

export function clearTheme(): void {
    if (typeof window === "undefined") return
    try {
        window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        // ignore
    }
    applyTheme(defaultTheme())
}

/* ── Contrast validation ──────────────────────────────────────────────────────
 * A theme is only usable if its text actually reads against its surfaces, so
 * every palette is checked against WCAG 2.1 before it can be saved. The same
 * rules are enforced server-side in users.py — this copy is for instant
 * feedback, not for security.
 */

export type Severity = "error" | "warning"

export interface ContrastRule {
    fg: string
    bg: string
    label: string
    /** Ratio below this fails and blocks saving. */
    min: number
    /** Ratio below this (but above `min`) is surfaced as a soft warning. */
    recommended?: number
    /** Non-blocking rules only ever produce warnings. */
    blocking?: boolean
}

/** WCAG AA: 4.5 for body text, 3.0 for UI boundaries and large text. */
export const AA_TEXT = 4.5
export const AA_UI = 3
export const AAA_TEXT = 7

export const CONTRAST_RULES: ContrastRule[] = [
    { fg: "foreground", bg: "background", label: "Body text on page", min: AA_TEXT, recommended: AAA_TEXT, blocking: true },
    { fg: "card-foreground", bg: "card", label: "Card text on card", min: AA_TEXT, recommended: AAA_TEXT, blocking: true },
    { fg: "popover-foreground", bg: "popover", label: "Popover text", min: AA_TEXT, recommended: AAA_TEXT, blocking: true },
    { fg: "primary-foreground", bg: "primary", label: "Text on primary buttons", min: AA_TEXT, blocking: true },
    { fg: "secondary-foreground", bg: "secondary", label: "Text on secondary", min: AA_TEXT, blocking: true },
    { fg: "accent-foreground", bg: "accent", label: "Text on accent / hover", min: AA_TEXT, blocking: true },
    { fg: "destructive-foreground", bg: "destructive", label: "Text on destructive", min: AA_TEXT, blocking: true },
    { fg: "muted-foreground", bg: "muted", label: "Muted text on muted fill", min: AA_TEXT, blocking: true },
    { fg: "muted-foreground", bg: "background", label: "Captions on page", min: AA_TEXT, blocking: true },
    { fg: "sidebar-foreground", bg: "sidebar", label: "Sidebar text", min: AA_TEXT, blocking: true },
    { fg: "sidebar-accent-foreground", bg: "sidebar-accent", label: "Sidebar active item", min: AA_TEXT, blocking: true },
    // Non-text contrast — these guard against invisible controls.
    { fg: "primary", bg: "background", label: "Primary against page", min: AA_UI, blocking: true },
    { fg: "ring", bg: "background", label: "Focus ring visibility", min: AA_UI, blocking: true },
    { fg: "border", bg: "background", label: "Borders against page", min: 1.2, blocking: false },
    { fg: "border", bg: "card", label: "Borders against cards", min: 1.2, blocking: false },
]

export function hexToRgb(hex: string): [number, number, number] | null {
    const m = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim())
    if (!m) return null
    let h = m[1]
    if (h.length === 3) h = h.split("").map((c) => c + c).join("")
    return [
        parseInt(h.slice(0, 2), 16),
        parseInt(h.slice(2, 4), 16),
        parseInt(h.slice(4, 6), 16),
    ]
}

/** WCAG relative luminance. */
export function relativeLuminance(rgb: [number, number, number]): number {
    const [r, g, b] = rgb.map((v) => {
        const s = v / 255
        return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
    })
    return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG contrast ratio, 1–21. Returns 0 when either colour is unparseable. */
export function contrastRatio(fg: string, bg: string): number {
    const a = hexToRgb(fg)
    const b = hexToRgb(bg)
    if (!a || !b) return 0
    const la = relativeLuminance(a)
    const lb = relativeLuminance(b)
    const [hi, lo] = la > lb ? [la, lb] : [lb, la]
    return (hi + 0.05) / (lo + 0.05)
}

export interface ContrastResult {
    rule: ContrastRule
    ratio: number
    severity: Severity | null
    message: string
}

/** Runs every rule against one mode's palette. */
export function checkContrast(values: TokenValues): ContrastResult[] {
    return CONTRAST_RULES.map((rule) => {
        const ratio = contrastRatio(values[rule.fg] ?? "", values[rule.bg] ?? "")
        const rounded = Math.round(ratio * 100) / 100

        let severity: Severity | null = null
        let message = `${rounded}:1 — passes`

        if (ratio < rule.min) {
            severity = rule.blocking ? "error" : "warning"
            message = `${rounded}:1 — needs at least ${rule.min}:1`
        } else if (rule.recommended && ratio < rule.recommended) {
            severity = "warning"
            message = `${rounded}:1 — passes AA, below AAA (${rule.recommended}:1)`
        }

        return { rule, ratio: rounded, severity, message }
    })
}

export interface ThemeAudit {
    results: Record<ThemeMode, ContrastResult[]>
    errors: number
    warnings: number
    /** False when either mode has a blocking failure. */
    valid: boolean
}

/** Audits both light and dark palettes together. */
export function auditTheme(theme: Record<ThemeMode, TokenValues>): ThemeAudit {
    const results = {
        light: checkContrast(theme.light),
        dark: checkContrast(theme.dark),
    }
    const all = [...results.light, ...results.dark]
    const errors = all.filter((r) => r.severity === "error").length
    const warnings = all.filter((r) => r.severity === "warning").length
    return { results, errors, warnings, valid: errors === 0 }
}

/**
 * Picks the best readable text colour for `bg`. Used by the "fix it for me"
 * affordance and by the generator.
 *
 * Prefers a soft off-black over pure black for looks, but falls back to pure
 * black/white when the surface sits in the awkward mid-luminance band where
 * off-black dips under AA. Pure black-or-white is always at least 4.58:1, so
 * this can never return a failing colour.
 */
export function bestForeground(bg: string): string {
    const soft = contrastRatio("#111111", bg) >= contrastRatio("#ffffff", bg) ? "#111111" : "#ffffff"
    if (contrastRatio(soft, bg) >= AA_TEXT) return soft
    return contrastRatio("#000000", bg) >= contrastRatio("#ffffff", bg) ? "#000000" : "#ffffff"
}

/* ── Multi-theme selection ────────────────────────────────────────────────────
 * next-themes only knows the fixed class list registered on the provider, so
 * rather than registering a class per saved theme (which would need the list at
 * SSR time and flash on first paint), `custom` / `custom-dark` act as *slots*.
 * The selected theme's palettes are injected into those two classes, and the
 * slug below records which theme is currently in the slot.
 */

export const SELECTED_SLUG_KEY = "satbuddy-custom-theme-slug"

export function loadSelectedSlug(): string | null {
    if (typeof window === "undefined") return null
    try {
        return window.localStorage.getItem(SELECTED_SLUG_KEY)
    } catch {
        return null
    }
}

export function saveSelectedSlug(slug: string | null): void {
    if (typeof window === "undefined") return
    try {
        if (slug) window.localStorage.setItem(SELECTED_SLUG_KEY, slug)
        else window.localStorage.removeItem(SELECTED_SLUG_KEY)
    } catch {
        // non-fatal: the slot styles are already applied in-page
    }
}

/**
 * Loads a saved theme into the custom slots: caches its palettes for the boot
 * script, records the slug, and repaints immediately.
 */
export function selectCustomTheme(
    slug: string,
    palettes: { light: TokenValues; dark: TokenValues }
): void {
    const theme = normalizeTheme(palettes)
    saveSelectedSlug(slug)
    persist(theme)
}

/** Writes the palette cache and repaints. */
function persist(theme: Record<ThemeMode, TokenValues>): void {
    if (typeof window !== "undefined") {
        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(theme))
        } catch {
            // ignore
        }
    }
    applyTheme(theme)
}

/* ── Palette generator ────────────────────────────────────────────────────────
 * "Random" here means a random *seed*, not random colours. A single base hue
 * drives the whole palette, a colour-harmony rule places the accent, and every
 * value is then fitted against the contrast rules — so a generated theme is
 * coherent by construction and always passes the audit.
 */

interface Hsl { h: number; s: number; l: number }

function hslToHex({ h, s, l }: Hsl): string {
    const sat = s / 100
    const lig = l / 100
    const c = (1 - Math.abs(2 * lig - 1)) * sat
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1))
    const m = lig - c / 2

    const [r, g, b] =
        h < 60 ? [c, x, 0] :
        h < 120 ? [x, c, 0] :
        h < 180 ? [0, c, x] :
        h < 240 ? [0, x, c] :
        h < 300 ? [x, 0, c] :
        [c, 0, x]

    const to255 = (v: number) =>
        Math.round((v + m) * 255).toString(16).padStart(2, "0")

    return `#${to255(r)}${to255(g)}${to255(b)}`
}

/**
 * Walks lightness in `dir` until the colour clears `min` contrast against every
 * background given. Falls back to white/black, which always satisfies AA text
 * contrast (the worst case for either is 4.58:1).
 */
function fitContrast(h: number, s: number, startL: number, backgrounds: string[], min: number, dir: 1 | -1): string {
    for (let l = startL; l >= 0 && l <= 100; l += dir * 2) {
        const hex = hslToHex({ h, s, l })
        if (backgrounds.every((bg) => contrastRatio(hex, bg) >= min)) return hex
    }
    return dir > 0 ? "#ffffff" : "#000000"
}

export const HARMONIES = [
    { name: "Complementary", offset: 180 },
    { name: "Analogous", offset: 32 },
    { name: "Triadic", offset: 120 },
    { name: "Split complementary", offset: 150 },
] as const

export interface GeneratedTheme {
    light: TokenValues
    dark: TokenValues
    /** Human-readable description of the seed, for the toast. */
    recipe: string
}

/**
 * Produces a complete, contrast-safe light + dark pair from a random seed.
 */
export function randomTheme(): GeneratedTheme {
    const hue = Math.floor(Math.random() * 360)
    const harmony = HARMONIES[Math.floor(Math.random() * HARMONIES.length)]
    const accentHue = (hue + harmony.offset) % 360
    // Muted, balanced, or vivid — keeps consecutive rolls from feeling samey.
    const sat = 55 + Math.floor(Math.random() * 35)

    // ── Light ────────────────────────────────────────────────────────────────
    const lBg = hslToHex({ h: hue, s: Math.min(sat, 40), l: 99 })
    const lCard = hslToHex({ h: hue, s: Math.min(sat, 30), l: 100 })
    const lMuted = hslToHex({ h: hue, s: Math.min(sat, 30), l: 96 })
    const lSecondary = hslToHex({ h: accentHue, s: Math.min(sat, 32), l: 95 })
    const lAccent = hslToHex({ h: accentHue, s: Math.min(sat, 40), l: 92 })
    const lSidebar = hslToHex({ h: hue, s: Math.min(sat, 34), l: 97 })
    const lSidebarAccent = hslToHex({ h: accentHue, s: Math.min(sat, 38), l: 93 })

    // Primary must read as a control against the page (3:1) and carry text.
    const lPrimary = fitContrast(hue, sat, 58, [lBg, lCard], AA_UI, -1)
    const lDestructive = fitContrast(2, 68, 48, [lBg], AA_UI, -1)

    const light: TokenValues = {
        background: lBg,
        foreground: fitContrast(hue, 25, 20, [lBg, lCard], AAA_TEXT, -1),
        card: lCard,
        "card-foreground": fitContrast(hue, 25, 20, [lCard], AAA_TEXT, -1),
        popover: lCard,
        "popover-foreground": fitContrast(hue, 25, 20, [lCard], AAA_TEXT, -1),
        primary: lPrimary,
        "primary-foreground": bestForeground(lPrimary),
        ring: lPrimary,
        secondary: lSecondary,
        "secondary-foreground": fitContrast(accentHue, 30, 28, [lSecondary], AA_TEXT, -1),
        muted: lMuted,
        // Captions sit on both the muted fill and the page, so fit against both.
        "muted-foreground": fitContrast(hue, 22, 46, [lMuted, lBg], AA_TEXT, -1),
        accent: lAccent,
        "accent-foreground": fitContrast(accentHue, 32, 24, [lAccent], AA_TEXT, -1),
        border: fitContrast(hue, 22, 88, [lBg, lCard], 1.25, -1),
        input: fitContrast(hue, 22, 86, [lBg, lCard], 1.25, -1),
        destructive: lDestructive,
        "destructive-foreground": bestForeground(lDestructive),
        sidebar: lSidebar,
        "sidebar-foreground": fitContrast(hue, 26, 26, [lSidebar], AA_TEXT, -1),
        "sidebar-primary": lPrimary,
        "sidebar-accent": lSidebarAccent,
        "sidebar-accent-foreground": fitContrast(accentHue, 30, 26, [lSidebarAccent], AA_TEXT, -1),
        "sidebar-border": fitContrast(hue, 22, 88, [lSidebar], 1.25, -1),
    }

    // ── Dark ─────────────────────────────────────────────────────────────────
    const dBg = hslToHex({ h: hue, s: Math.min(sat, 26), l: 7 })
    const dCard = hslToHex({ h: hue, s: Math.min(sat, 24), l: 11 })
    const dMuted = hslToHex({ h: hue, s: Math.min(sat, 22), l: 17 })
    const dSecondary = hslToHex({ h: accentHue, s: Math.min(sat, 22), l: 18 })
    const dAccent = hslToHex({ h: accentHue, s: Math.min(sat, 26), l: 22 })
    const dSidebar = hslToHex({ h: hue, s: Math.min(sat, 26), l: 9 })
    const dSidebarAccent = hslToHex({ h: accentHue, s: Math.min(sat, 26), l: 20 })

    const dPrimary = fitContrast(hue, Math.min(sat, 80), 62, [dBg, dCard], AA_UI, 1)
    const dDestructive = fitContrast(2, 70, 62, [dBg], AA_UI, 1)

    const dark: TokenValues = {
        background: dBg,
        foreground: fitContrast(hue, 14, 94, [dBg, dCard], AAA_TEXT, 1),
        card: dCard,
        "card-foreground": fitContrast(hue, 14, 94, [dCard], AAA_TEXT, 1),
        popover: dCard,
        "popover-foreground": fitContrast(hue, 14, 94, [dCard], AAA_TEXT, 1),
        primary: dPrimary,
        "primary-foreground": bestForeground(dPrimary),
        ring: dPrimary,
        secondary: dSecondary,
        "secondary-foreground": fitContrast(accentHue, 14, 92, [dSecondary], AA_TEXT, 1),
        muted: dMuted,
        "muted-foreground": fitContrast(hue, 12, 64, [dMuted, dBg], AA_TEXT, 1),
        accent: dAccent,
        "accent-foreground": fitContrast(accentHue, 14, 92, [dAccent], AA_TEXT, 1),
        border: fitContrast(hue, 20, 22, [dBg, dCard], 1.25, 1),
        input: fitContrast(hue, 20, 26, [dBg, dCard], 1.25, 1),
        destructive: dDestructive,
        "destructive-foreground": bestForeground(dDestructive),
        sidebar: dSidebar,
        "sidebar-foreground": fitContrast(hue, 14, 92, [dSidebar], AA_TEXT, 1),
        "sidebar-primary": dPrimary,
        "sidebar-accent": dSidebarAccent,
        "sidebar-accent-foreground": fitContrast(accentHue, 14, 92, [dSidebarAccent], AA_TEXT, 1),
        "sidebar-border": fitContrast(hue, 20, 24, [dSidebar], 1.25, 1),
    }

    return { light, dark, recipe: `${harmony.name} · hue ${hue}°` }
}

/**
 * Generates a palette that passes the audit outright. The fitting above makes
 * that the normal case; the retries cover the rare seed where a rounded hex
 * lands a hair under a threshold.
 */
export function randomValidTheme(attempts = 12): GeneratedTheme {
    let last = randomTheme()
    for (let i = 0; i < attempts; i++) {
        last = randomTheme()
        if (auditTheme({ light: last.light, dark: last.dark }).valid) return last
    }
    return last
}
